/**
 * Logs API Service
 * Fetch API call logs from backend
 */

import api from './api';
import type { ApiResponse } from '../types';

export interface LogEntry {
  _id: string;
  type: 'verso' | 'google_eta' | 'mapbox_eta' | 'core_api' | 'reassign' | 'route_publish' | 'task_assign' | 'task_unassign' | 'error';
  level: 'debug' | 'info' | 'warn' | 'error';
  action: string;
  payload?: Record<string, unknown>;
  response?: Record<string, unknown>;
  error?: {
    message: string;
    stack?: string;
    code?: string;
  };
  duration_ms?: number;
  route_run_id?: string;
  route_id?: string;
  task_uid?: string;
  driver_uid?: string;
  created_at: string;
}

export interface LogsResponse {
  logs: LogEntry[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface LogTypeCount {
  type: string;
  count: number;
  lastLog: string;
}

export interface GetLogsParams {
  type?: string;
  level?: string;
  action?: string;
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
}

/**
 * Get logs with optional filtering
 */
export async function getLogs(params: GetLogsParams = {}): Promise<LogsResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.type) queryParams.append('type', params.type);
  if (params.level) queryParams.append('level', params.level);
  if (params.action) queryParams.append('action', params.action);
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.offset) queryParams.append('offset', params.offset.toString());
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  const response = await api.get<ApiResponse<LogsResponse>>(
    `/logs?${queryParams.toString()}`
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch logs');
  }

  return response.data.data;
}

/**
 * Get log type counts
 */
export async function getLogTypes(): Promise<LogTypeCount[]> {
  const response = await api.get<ApiResponse<LogTypeCount[]>>('/logs/types');

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch log types');
  }

  return response.data.data;
}

export interface EtaUsage {
  provider: 'mapbox' | 'google';
  calls: number;
  budget: number;
  percent: number;
  warn: boolean;
  blocked: boolean;
  month: string;
  free_tier_limit: number;
}

/**
 * Get month-to-date ETA provider usage vs budget (cost control)
 */
export async function getEtaUsage(): Promise<EtaUsage> {
  const response = await api.get<ApiResponse<EtaUsage>>('/logs/eta-usage');

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch ETA usage');
  }

  return response.data.data;
}

/**
 * Get a single log by ID
 */
export async function getLog(id: string): Promise<LogEntry> {
  const response = await api.get<ApiResponse<LogEntry>>(`/logs/${id}`);

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch log');
  }

  return response.data.data;
}

/**
 * Delete logs
 */
export async function deleteLogs(params: { type?: string; olderThanDays?: number } = {}): Promise<{ deletedCount: number }> {
  const queryParams = new URLSearchParams();
  
  if (params.type) queryParams.append('type', params.type);
  if (params.olderThanDays) queryParams.append('olderThanDays', params.olderThanDays.toString());

  const response = await api.delete<ApiResponse<{ deletedCount: number }>>(
    `/logs?${queryParams.toString()}`
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || 'Failed to delete logs');
  }

  return response.data.data;
}
